import * as React from "react";

import { Button, ButtonProps, KeyTrap, Svg } from "@impero-com/ui";
import remove from "@impero-com/ui/img/remove.svg";
import { Checkbox, Input, InputNumber, InputRef, Radio, Select } from "antd";
import { useNavigate } from "react-router-dom";

import { assertNever } from "../../utils/obj";
import { nullToEmpty } from "../../utils/string";
import { H1, H2, Label } from "../html";

import * as theme from "./theme.css";

// DO NOT REMOVE, used for dialogs
const PAGE_CLASS_NAME = "page";

const Page = (props: {
    title: React.ReactNode;
    shrinkTitle?: boolean;
    getHeadingRight?: () => React.ReactNode;
    noPadding?: boolean;
    children?: React.ReactNode;
}) => {
    const { title, shrinkTitle, getHeadingRight, noPadding, children } = props;
    const titleHeading = <H1 className={theme.pageTitle}>{title}</H1>;
    const headingRight = getHeadingRight ? getHeadingRight() : null;
    return (
        <div className={PAGE_CLASS_NAME}>
            {headingRight ? (
                <div className={theme.pageHeading}>
                    <div
                        className={
                            shrinkTitle ? undefined : theme.pageHeadingMiddle
                        }
                    >
                        {titleHeading}
                    </div>
                    {headingRight}
                </div>
            ) : (
                titleHeading
            )}
            <PageContent noPadding={noPadding}>{children}</PageContent>
        </div>
    );
};

const NoClassPage = (props: {
    title: React.ReactNode;
    shrinkTitle?: boolean;
    getHeadingRight?: () => React.ReactNode;
    children?: React.ReactNode;
}) => {
    const { title, shrinkTitle, getHeadingRight, children } = props;
    const titleHeading = <H1 className={theme.pageTitle}>{title}</H1>;
    const headingRight = getHeadingRight ? getHeadingRight() : null;
    return (
        <div className={PAGE_CLASS_NAME}>
            {headingRight ? (
                <div className={theme.pageHeading}>
                    <div
                        className={
                            shrinkTitle ? undefined : theme.pageHeadingMiddle
                        }
                    >
                        {titleHeading}
                    </div>
                    {headingRight}
                </div>
            ) : (
                titleHeading
            )}
            <div>{children}</div>
        </div>
    );
};

function PageContent(props: {
    noPadding?: boolean;
    children?: React.ReactNode;
}) {
    const { noPadding, children } = props;
    return (
        <div
            className={
                noPadding ? theme.pageContentNoPadding : theme.pageContent
            }
        >
            {children}
        </div>
    );
}

function Section(props: {
    title?: string;
    getHeadingRight?: () => React.ReactNode;
    children?: React.ReactNode;
}) {
    const { title, getHeadingRight, children } = props;
    const titleHeading = title ? <H2>{title}</H2> : null;
    const headingRight = getHeadingRight ? getHeadingRight() : null;
    return (
        <div className={theme.section}>
            {headingRight ? (
                <div className={theme.sectionHeading}>
                    <div className={theme.sectionHeadingMiddle}>
                        {titleHeading}
                    </div>
                    {headingRight}
                </div>
            ) : (
                titleHeading
            )}
            <div>{children}</div>
        </div>
    );
}

function SubSection(props: { children?: React.ReactNode }) {
    const { children } = props;
    return (
        <div className={theme.subSection}>
            <hr className={theme.subSectionSeparator} />
            {children}
        </div>
    );
}

function FormSection(props: FormSectionProps) {
    const { className, imperoForm, children } = props;
    const classNames = [theme.formSection, nullToEmpty(className)];
    if (imperoForm) {
        classNames.push(theme.imperoFormSection);
    }
    return <div className={classNames.join(" ")}>{children}</div>;
}

interface FormSectionProps {
    className?: string;
    imperoForm?: boolean;
    children?: React.ReactNode;
}

function FormBlock(props: FormBlockProps) {
    const {
        className,
        children,
        eraseSideBorders,
        eraseTopBorder,
        eraseBottomBorder,
        sideBorders,
        topBorder,
        bottomBorder,
        padding,
    } = props;
    const classNames = [nullToEmpty(className)];
    if (eraseSideBorders) {
        classNames.push(theme.eraseSideBorders);
    }
    if (eraseTopBorder) {
        classNames.push(theme.eraseTopBorder);
    }
    if (eraseBottomBorder) {
        classNames.push(theme.eraseBottomBorder);
    }
    if (sideBorders) {
        classNames.push(theme.sideBorders);
    }
    if (topBorder) {
        classNames.push(theme.topBorder);
    }
    if (bottomBorder) {
        classNames.push(theme.bottomBorder);
    }
    if (padding) {
        classNames.push(theme.blockPadding);
    }
    return <div className={classNames.join(" ")}>{children}</div>;
}

interface FormBlockProps {
    eraseSideBorders?: boolean;
    eraseTopBorder?: boolean;
    eraseBottomBorder?: boolean;
    sideBorders?: boolean;
    topBorder?: boolean;
    bottomBorder?: boolean;
    padding?: boolean;
    className?: string;
    children?: React.ReactNode;
}

function FormFrame(props: { className?: string; children?: React.ReactNode }) {
    const { className, children } = props;
    return (
        <div className={`${theme.formFrame} ${nullToEmpty(className)}`}>
            {children}
        </div>
    );
}

interface ImperoFormDisplay {
    type: "ImperoForm";
}

interface InlineLabelBeforeDisplay {
    type: "Inline";
    labelAfter?: undefined;
}

interface InlineLabelAfterDisplay {
    type: "Inline";
    labelAfter: true;
}

type InlineDisplay = InlineLabelBeforeDisplay | InlineLabelAfterDisplay;

interface InlineBlockDisplay {
    type: "InlineBlock";
}

interface BlockDisplay {
    type: "Block";
}

type InputDisplay =
    | ImperoFormDisplay
    | InlineDisplay
    | InlineBlockDisplay
    | BlockDisplay;
type TextAreaDisplay =
    | ImperoFormDisplay
    | InlineLabelBeforeDisplay
    | InlineBlockDisplay
    | BlockDisplay;

function Labelized(props: LabelizedProps) {
    const { display, label, className, labelClassName, children } = props;
    if (!label) {
        return <span>{children}</span>;
    }
    const realDisplay = display || { type: "ImperoForm" };
    let fullClassName: string;
    let labelAfter = false;
    switch (realDisplay.type) {
        case "ImperoForm":
            fullClassName = `${theme.imperoFormLabel} ${
                theme.labelBefore
            } ${nullToEmpty(className)}`;
            break;
        case "Inline":
            fullClassName = `${theme.inlineLabel} ${
                realDisplay.labelAfter ? theme.labelAfter : theme.labelBefore
            } ${nullToEmpty(className)}`;
            labelAfter = !!realDisplay.labelAfter;
            break;
        case "InlineBlock":
            fullClassName = `${theme.inlineBlockLabel} ${
                theme.labelBefore
            } ${nullToEmpty(className)}`;
            break;
        case "Block":
            fullClassName = `${theme.blockLabel} ${
                theme.labelBefore
            } ${nullToEmpty(className)}`;
            break;
        default:
            return assertNever(realDisplay);
    }
    const wrappedChildren = (
        <div className={theme.labelContent}>{children}</div>
    );
    return labelAfter === true ? (
        <Label className={fullClassName}>
            {wrappedChildren} {label}
        </Label>
    ) : (
        <Label className={fullClassName}>
            <span
                className={`${theme.labelValue} ${nullToEmpty(labelClassName)}`}
            >
                {label}
            </span>{" "}
            {wrappedChildren}
        </Label>
    );
}

interface LabelizedProps {
    display?: InputDisplay;
    label: string | undefined | null;
    className?: string;
    labelClassName?: string;
    children?: React.ReactNode;
}

function Deletable(props: DeletableProps) {
    const { className, children, onDelete } = props;
    return (
        <div className={`${theme.deletable} ${nullToEmpty(className)}`}>
            <div className={theme.deletableInner}>{children}</div>
            {onDelete ? (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        onDelete();
                    }}
                >
                    <Svg src={remove} />
                </a>
            ) : null}
        </div>
    );
}

interface DeletableProps {
    className?: string;
    onDelete: () => void;
    children?: React.ReactNode;
}

class FormText extends React.Component<FormTextProps> {
    private elt = React.createRef<InputRef>();
    componentDidMount() {
        if (this.elt.current && this.props.focusOnMount) {
            this.elt.current.focus();
        }
    }
    render() {
        const {
            id,
            display,
            label,
            placeholder,
            disabled,
            autoComplete,
            className,
            labelClassName,
            defaultValue,
            maxLength,
            noKeyTrap,
            addonBefore,
            addonAfter,
            onChange,
            onBlur,
            onClick,
            onFocus,
        } = this.props;
        const realDisplay = display || { type: "ImperoForm" };
        const inputElt = (
            <Input
                ref={this.elt}
                className={`${theme.formText} ${nullToEmpty(className)}`}
                type="text"
                id={id}
                placeholder={placeholder}
                disabled={disabled}
                defaultValue={defaultValue}
                maxLength={maxLength}
                autoComplete={autoComplete}
                addonBefore={addonBefore}
                addonAfter={addonAfter}
                onClick={onClick}
                onBlur={(e) => onBlur(e.target.value)}
                onFocus={onFocus ? (e) => onFocus(e.target.value) : undefined}
                onChange={
                    onChange ? (e) => onChange(e.target.value) : undefined
                }
            />
        );
        return (
            <Labelized
                display={realDisplay}
                label={label}
                labelClassName={labelClassName}
            >
                {noKeyTrap === true ? (
                    inputElt
                ) : (
                    <KeyTrap
                        onLocalKeyDown={{
                            enter: () => this.handleEnter(),
                            esc: () => this.handleEscape(),
                        }}
                    >
                        {inputElt}
                    </KeyTrap>
                )}
            </Labelized>
        );
    }
    value() {
        if (this.elt.current?.input) {
            return this.elt.current.input.value;
        } else {
            return null;
        }
    }
    blur() {
        if (this.elt.current) {
            this.elt.current.blur();
        }
    }
    clear() {
        if (this.elt.current?.input) {
            this.elt.current.input.value = "";
        }
    }
    select() {
        if (this.elt.current) {
            this.elt.current.select();
        }
    }
    focus() {
        if (this.elt.current) {
            this.elt.current.focus();
        }
    }
    private handleEnter(): boolean {
        const { onEnter } = this.props;
        if (this.elt.current) {
            if (onEnter && this.elt.current?.input?.value) {
                const value: string = this.elt.current.input.value;
                onEnter(value);
            }
            // blur and propagate
            this.elt.current.blur();
        }
        return true;
    }
    private handleEscape(): boolean {
        const { onEscape } = this.props;
        if (onEscape) {
            onEscape();
        }
        return true;
    }
}

// That is the basement
interface FormTextProps {
    id: string;
    display?: InputDisplay;
    label?: string;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    labelClassName?: string;
    focusOnMount?: boolean;
    noKeyTrap?: boolean;
    maxLength?: number;
    autoComplete?: string;
    addonBefore?: React.ReactNode;
    addonAfter?: React.ReactNode;
    onFocus?: (value: string) => void;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
    onEnter?: (value: string) => void;
    onEscape?: () => void;
    defaultValue: string | undefined;
    onBlur: (newValue: string) => void;
    onChange?: (newValue: string) => void;
}

/*
 * Input of type "text", controlled
 */
class ControlledFormText extends React.Component<ControlledFormTextProps> {
    render() {
        const {
            id,
            display,
            label,
            placeholder,
            disabled,
            className,
            labelClassName,
            value,
            maxLength,
            onChange,
            onBlur,
        } = this.props;
        const realDisplay = display || { type: "ImperoForm" };
        const inputElt = (
            <Input
                className={`${theme.formText} ${nullToEmpty(className)}`}
                type="text"
                id={id}
                placeholder={placeholder}
                disabled={disabled}
                value={value}
                maxLength={maxLength}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
            />
        );
        return (
            <Labelized
                display={realDisplay}
                label={label}
                labelClassName={labelClassName}
            >
                {inputElt}
            </Labelized>
        );
    }
}

interface ControlledFormTextProps {
    id: string;
    display?: InputDisplay;
    label?: string;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    labelClassName?: string;
    maxLength?: number;
    value: string | undefined;
    onChange: (newValue: string) => void;
    onBlur?: (newValue: string) => void;
}

function FormTextArea(props: FormTextAreaProps) {
    const {
        id,
        display,
        placeholder,
        label,
        disabled,
        className,
        defaultValue,
        onBlur,
    } = props;
    const realDisplay = display || { type: "ImperoForm" };
    return (
        <Labelized display={realDisplay} label={label}>
            <Input.TextArea
                className={`${theme.formTextArea} ${nullToEmpty(className)}`}
                id={id}
                placeholder={placeholder}
                defaultValue={defaultValue}
                disabled={disabled}
                onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
            />
        </Labelized>
    );
}

interface BaseFormTextAreaProps {
    id: string;
    display?: TextAreaDisplay;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    className?: string;
}

interface FormTextAreaProps extends BaseFormTextAreaProps {
    defaultValue: string | undefined;
    onBlur: (newValue: string) => void;
}

function ControlledFormTextArea(props: ControlledFormTextAreaProps) {
    const {
        id,
        display,
        placeholder,
        label,
        disabled,
        className,
        value,
        autosize,
        onChange,
    } = props;
    const realDisplay = display || { type: "ImperoForm" };
    const textareaProps = {
        ...{ id, placeholder, value: value || "", disabled },
        className: `${theme.formTextArea} ${nullToEmpty(className)}`,
        onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange(e.target.value),
        autosize,
    };
    return (
        <Labelized display={realDisplay} label={label}>
            {<Input.TextArea {...textareaProps} />}
        </Labelized>
    );
}

interface ControlledFormTextAreaProps extends BaseFormTextAreaProps {
    value: string | undefined;
    onChange: (newValue: string) => void;
    autosize?: boolean;
}

function FormSelect(props: FormSelectProps) {
    const {
        id,
        display,
        className,
        label,
        disabled,
        value,
        options,
        onChange,
        dropdownMatchSelectWidth = false,
    } = props;
    return (
        <Labelized display={display} label={label}>
            <Select
                className={`${theme.formSelect} ${nullToEmpty(className)}`}
                id={id}
                value={value}
                disabled={disabled}
                onChange={onChange}
                dropdownMatchSelectWidth={dropdownMatchSelectWidth}
            >
                {options.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                    </Select.Option>
                ))}
            </Select>
        </Labelized>
    );
}

interface FormSelectProps {
    id: string;
    display?: InputDisplay;
    className?: string;
    label?: string;
    disabled?: boolean;
    value: string | undefined;
    options: Array<FormOption>;
    /**
     * If true, the dropdown's with will be the same as the select's width.
     * Default: false - in most cases the select has fixed, short width, and the dropdown should be wider.
     */
    dropdownMatchSelectWidth?: boolean;
    onChange?: (newValue: string) => void;
}

interface FormOption {
    label: string;
    value: string;
}

function FormCheckbox(props: FormCheckboxProps) {
    const { id, className, disabled, label, checked, onChange } = props;
    return (
        <div className={`${theme.checkbox} ${nullToEmpty(className)}`}>
            <Checkbox
                id={id}
                checked={checked}
                disabled={disabled}
                className={theme.checkboxLabel}
                onChange={onChange ? () => onChange(!checked) : undefined}
            >
                {label}
            </Checkbox>
        </div>
    );
}

interface FormCheckboxProps {
    id: string;
    disabled?: boolean;
    label?: string;
    checked: boolean;
    className?: string;
    onChange?: (checked: boolean) => void;
}

function FormRadio(props: FormRadioProps) {
    const { id, className, disabled, label, checked, onChange, onReset } =
        props;
    return (
        <div className={`${theme.radio} ${nullToEmpty(className)}`}>
            <Radio
                id={id}
                checked={checked}
                disabled={disabled}
                className={theme.radioLabel}
                onClick={
                    onReset
                        ? () => {
                              if (checked) {
                                  onReset();
                              }
                          }
                        : undefined
                }
                onChange={onChange ? () => onChange(!checked) : undefined}
            >
                {label}
            </Radio>
        </div>
    );
}

interface FormRadioProps {
    id: string;
    label?: string;
    disabled?: boolean;
    checked: boolean;
    className?: string;
    onChange?: (checked: boolean) => void;
    onReset?: () => void;
}

class FormInputNumber extends React.Component<FormInputNumberProps> {
    render() {
        const {
            id,
            display,
            label,
            disabled,
            className,
            labelClassName,
            value,
            min,
            max,
            onChange,
        } = this.props;
        const realDisplay = display || { type: "ImperoForm" };
        const inputElt = (
            <InputNumber
                id={id}
                min={min}
                max={max}
                disabled={disabled}
                value={value}
                className={`${theme.formNumber} ${nullToEmpty(className)}`}
                onChange={(value) => {
                    // Catch undefined, null, "", NaN, and out of range values
                    if (
                        value !== undefined &&
                        value !== null &&
                        typeof value === "number" &&
                        !isNaN(value) &&
                        value >= min &&
                        value <= max
                    ) {
                        onChange(value);
                    }
                }}
            />
        );
        return (
            <Labelized
                display={realDisplay}
                label={label}
                labelClassName={labelClassName}
            >
                {inputElt}
            </Labelized>
        );
    }
}

interface FormInputNumberProps {
    id: string;
    display?: InputDisplay;
    label?: string;
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
    min: number;
    max: number;
    value: number;
    onChange: (value: number) => void;
}

function FormButtonGroup(props: { children: React.ReactNode }) {
    return (
        <div className={theme.formGroup}>
            {React.Children.map(props.children, (child, index) => {
                return (
                    <div
                        key={index}
                        className={
                            index > 0
                                ? theme.formGroupAdditionalChild
                                : theme.formGroupFirstChild
                        }
                    >
                        {child}
                    </div>
                );
            })}
        </div>
    );
}

function FormButton(props: FormButtonProps) {
    const { id, label, disabled, type, className, loading, danger } = props;
    const actualType = type !== undefined ? type : FormButtonType.Secondary;
    const onClick = (props as ClickFormButtonProps).onClick;
    const href = (props as LinkFormButtonProps).href;
    const target = (props as LinkFormButtonProps).target;

    const variant = VARIANT_BY_FORM_BUTTON_TYPE[actualType];

    // An href has to render an anchor, which the Button only does via asChild.
    if (href !== undefined) {
        return (
            <Button
                variant={variant}
                danger={danger}
                className={className}
                asChild
            >
                <a id={id} href={href} target={target}>
                    {label}
                </a>
            </Button>
        );
    }

    return (
        <Button
            id={id}
            variant={variant}
            danger={danger}
            className={className}
            disabled={disabled}
            loading={loading}
            onClick={disabled ? undefined : onClick}
        >
            {label}
        </Button>
    );
}

enum FormButtonType {
    Primary,
    Secondary,
    Link,
    Delete,
}

const VARIANT_BY_FORM_BUTTON_TYPE = {
    [FormButtonType.Primary]: "primary",
    [FormButtonType.Secondary]: "default",
    [FormButtonType.Delete]: "destructive",
    [FormButtonType.Link]: "link",
} as const satisfies Record<FormButtonType, ButtonProps["variant"]>;

interface BaseFormButtonProps {
    id: string;
    label: string;
    disabled?: boolean;
    className?: string;
    loading?: boolean;
    danger?: boolean;
}

interface ClickFormButtonProps extends BaseFormButtonProps {
    type?: FormButtonType;
    onClick: React.MouseEventHandler<any>;
}

interface LinkFormButtonProps extends BaseFormButtonProps {
    // We don't want undefined here as the default is different than the default above.
    type: FormButtonType;
    href: string;
    target?: string;
}

type FormButtonProps = ClickFormButtonProps | LinkFormButtonProps;

// This is a react router link and shall never be a regular hyperlink.
function FormLinkButton(props: FormLinkButtonProps) {
    const { id, label, className, type, href } = props;
    const navigate = useNavigate();
    return (
        <FormButton
            id={id}
            type={type}
            label={label}
            className={className}
            onClick={() => {
                navigate(href);
            }}
        />
    );
}

interface FormLinkButtonProps {
    id: string;
    label: string;
    type?: FormButtonType;
    className?: string;
    href: string;
}

function PropertiesFrame(props: PropertiesFrameProps) {
    const { properties } = props;
    return (
        <ul className={theme.propertiesFrame}>
            {properties.map((prop, index) => {
                return (
                    <li key={index}>
                        <div className={theme.propertyContainer}>
                            <div className={theme.propertyImageContainer}>
                                <img
                                    className={prop.imageClassName}
                                    src={prop.image}
                                    alt={prop.imageAlt || ""}
                                />
                            </div>
                            <div className={theme.propertyBody}>
                                <div className={theme.propertyLabel}>
                                    {prop.label}
                                </div>
                                <div>{prop.content}</div>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

interface PropertiesFrameProps {
    properties: Array<PropertyDescriptor>;
}

interface PropertyDescriptor {
    image: string;
    imageAlt?: string;
    imageClassName: string;
    label: string;
    content: React.ReactNode;
}

function SmallActionButton(props: SmallActionButtonProps) {
    const { image, onClick, disabled } = props;
    return (
        <span
            className={
                disabled
                    ? theme.smallActionButton
                    : theme.smallActionButtonEnabled
            }
            onClick={disabled ? undefined : onClick}
        >
            <img src={image} />
        </span>
    );
}

interface SmallActionButtonProps {
    image: string;
    onClick: () => void;
    disabled?: boolean;
}

function BlankSmallActionButton() {
    return <span className={theme.smallActionButton} />;
}

export type { InputDisplay, TextAreaDisplay, FormOption, PropertyDescriptor };
export {
    PAGE_CLASS_NAME,
    Page,
    NoClassPage,
    PageContent,
    Labelized,
    Deletable,
    Section,
    SubSection,
    FormSection,
    FormBlock,
    FormFrame,
    FormText,
    ControlledFormText,
    FormTextArea,
    ControlledFormTextArea,
    FormSelect,
    FormCheckbox,
    FormRadio,
    FormInputNumber,
    FormButtonGroup,
    FormButton,
    FormButtonType,
    FormLinkButton,
    PropertiesFrame,
    SmallActionButton,
    BlankSmallActionButton,
    theme,
};
